import { clsx } from 'clsx';
import pickBy from 'lodash-es/pickBy';
import type {
  ElementType,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  ReactNode,
} from 'react';
import { forwardRef } from 'react';

import { getAccessibilityProps } from '../../internal/utils/getAccessibilityProps';
import type { AccessibilityProps } from '../../internal/utils/getAccessibilityProps';
import type { BorderRadiusKey } from '../../theme/theme.types';
import type { ColorKey } from '../../theme/themeColors.types';
import type { ReeferBaseProps } from '../../utils/baseProps.types';
import { toCSSSize } from '../../utils/css';
import { fakeButtonProps, handleEnterKey } from '../../utils/handleEnterKey';
import { spacingCSSVars } from '../../utils/spacing';
import type {
  MarginProperties,
  PaddingProperties,
} from '../../utils/spacing.types';

import styles from './box.module.css';

type BoxOverflowType =
  'auto' | 'clip' | 'hidden' | 'scroll' | 'visible' | 'inherit';

export interface BoxProps
  extends
    AccessibilityProps,
    ReeferBaseProps,
    MarginProperties,
    PaddingProperties {
  /** Render the component as this HTML element. Defaults to `div`. */
  as?: keyof JSX.IntrinsicElements;

  /** Add a background color to the component */
  background?: ColorKey;

  /** Add a 1px solid border around the component */
  border?: ColorKey | 'none';

  /** Add a border radius to the component using available theme sizes */
  borderRadius?: BorderRadiusKey;

  /** Optionally change the border width, defaults to 1px */
  borderWidth?: string;

  /** CSS bottom attribute of element */
  bottom?: string | number;

  /** Child elements of the component */
  children?: ReactNode;

  'data-testid'?: string;

  /** Set a height, accepts any valid `height` value */
  height?: string | number;

  /** For attribute, for labels only, must match id of input label is for. */
  htmlFor?: string;

  /** CSS left attribute of element */
  left?: string | number;

  /** Set item `max-height`, accepts any valid `max-height` value */
  maxHeight?: string | number;

  /** Set item `max-width`, accepts any valid `max-width` value */
  maxWidth?: string | number;

  /** Set item `min-height`, accepts any valid `min-height` value */
  minHeight?: string | number;

  /** Set item `min-width`, accepts any valid `min-width` value */
  minWidth?: string | number;

  /** An optional `onClick` handler */
  onClick?: ((e: MouseEvent) => void) | (() => void);

  /** An optional `onMouseEnter` handler */
  onMouseEnter?: (event: MouseEvent<HTMLDivElement>) => void;

  /** An optional `onMouseLeave` handler */
  onMouseLeave?: (event: MouseEvent<HTMLDivElement>) => void;

  /** Set item `overflow`,  accepts any of the listed `overflow` values. Use `overflowX` and `overflowY` props to set those separately. */
  overflow?: BoxOverflowType;

  /** Set item `overflow-x`, accepts any of the listed `overflow-x` values */
  overflowX?: BoxOverflowType;

  /** Set item `overflow-y`, accepts any of the listed `overflow-y` values */
  overflowY?: BoxOverflowType;

  /** Part names allows CSS to select and style specific elements in a shadow tree via the ::part pseudo-element. */
  part?: string;

  /** CSS position attribute of element */
  position?:
    'absolute' | 'fixed' | 'inherit' | 'relative' | 'static' | 'sticky';

  /** CSS right attribute of element */
  right?: string | number;

  /**
   * HTML `title` attribute, displays the supplied text as a tooltip. Note: Use
   * `title` sparingly as it is not very accessible and doesn't show up on touch
   * interfaces.
   */
  title?: string;

  /** CSS top attribute of element */
  top?: string | number;

  /** Set the type of the element, useful for fake buttons */
  type?: HTMLButtonElement['type'];

  /** Set a width, accepts any valid `width` value */
  width?: string | number;

  /** Set item `z-index`, accepts any valid `z-index` value */
  zIndex?: string | number;
}

/**
 * The `Box` component is a wrapping container component with `margin` and `padding`
 * spacing props. Additional style props including `background`, `border`, `borderRadius`, `height` and `width` are also available.
 */

export const Box = forwardRef<HTMLElement, BoxProps>(function Box(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-live': ariaLive,
    ariaLabel: ariaLabel_toBeDeprecated,
    ariaLabelledBy: ariaLabelledBy_toBeDeprecated,
    as = 'div',
    background,
    border,
    borderRadius,
    borderWidth = '1px',
    bottom = 'auto',
    children,
    className,
    'data-testid': testId,
    height = 'auto',
    htmlFor,
    id,
    left = 'auto',
    maxHeight = 'none',
    maxWidth = 'none',
    minHeight = 'auto',
    minWidth = 'auto',
    onClick,
    onMouseEnter,
    onMouseLeave,
    overflow,
    overflowX: overflowXProp,
    overflowY: overflowYProp,
    position,
    right = 'auto',
    role,
    style,
    title,
    top = 'auto',
    width = 'auto',
    zIndex = 'auto',
    part,
    ...props
  },
  forwardedRef
) {
  const a11yProps = getAccessibilityProps({
    ariaLabel: ariaLabel || ariaLabel_toBeDeprecated,
    ariaLabelledBy: ariaLabelledBy || ariaLabelledBy_toBeDeprecated,
    'aria-live': ariaLive,
    role,
    onClick,
  });

  const dataAttributes = pickBy(props, (value: string, key: string) =>
    key.startsWith('data-')
  );
  const spacingProps = pickBy(
    props,
    (value: string, key: string) => !key.startsWith('data-')
  );

  const RenderAs = as as ElementType;

  const overflowX = overflowXProp || overflow;
  const overflowY = overflowYProp || overflow;

  return (
    <RenderAs
      className={clsx(
        className,
        styles['box'],
        background &&
          background !== 'gradient-primary' &&
          styles[`box__backgroundColor_${background}`],
        border &&
          border !== 'gradient-primary' &&
          styles[`box__border_${border}`],
        borderRadius && styles[`box__borderRadius_${borderRadius}`],
        overflowX && styles[`box__overflowX_${overflowX}`],
        overflowY && styles[`box__overflowY_${overflowY}`],
        position && styles[`box__position_${position}`],
        {
          [styles['box__clickable']]: !!onClick,
        }
      )}
      data-testid={testId}
      {...dataAttributes}
      htmlFor={htmlFor}
      id={id}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyUp={(event: KeyboardEvent) =>
        handleEnterKey(event, onClick as unknown as KeyboardEventHandler)
      }
      {...(onClick && fakeButtonProps)}
      ref={forwardedRef}
      style={{
        '--box-border-width': borderWidth,
        '--box-bottom': toCSSSize(bottom),
        '--box-height': toCSSSize(height),
        '--box-left': toCSSSize(left),
        '--box-max-height': toCSSSize(maxHeight),
        '--box-max-width': toCSSSize(maxWidth),
        '--box-min-height': toCSSSize(minHeight),
        '--box-min-width': toCSSSize(minWidth),
        '--box-right': toCSSSize(right),
        '--box-top': toCSSSize(top),
        '--box-width': toCSSSize(width),
        '--box-z-index': zIndex,
        ...spacingCSSVars(spacingProps),
        ...style,
      }}
      {...a11yProps}
      part={part}
      title={title}
    >
      {children}
    </RenderAs>
  );
});
